import { Tooltip } from '@mui/material';
import React, { FC } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { ComposeNodeType } from '../../../../containers/compose/props';
import useIsComposeOpen from '../../../../hooks/router/search/useIsComposeOpen';
import translate from '../../../../i18n/translate';
import { DialogId } from '../../../../models/dialog';
import { ComposeTypeParam } from '../../../../models/pageParams';
import { getComposeMinimizeToggleUrl } from '../../../../routes/urls/searchParams/compose';
import ConfirmPopover from '../../../MUI/ConfirmPopover';
import MuiIcon from '../../../MUI/MuiIcon';

import * as Styled from './styled';

type Props = {
  composeType: ComposeTypeParam;
  isDraftChanged: boolean;
  nodeType: ComposeNodeType;
  onClose: () => void;
  title: string;
  titleId?: string;
};

/**
 * Compose form modal header used for modal header and for minimized version
 *
 * @param props                Props passed to the component
 * @param props.composeType    Type of the compose (post or task)
 * @param props.isDraftChanged Whether the draft has been changed
 * @param props.nodeType       Type of the compose form (MESSAGE or TEMPLATE)
 * @param props.onClose        Callback for close button
 * @param props.title          The title of the modal
 * @param props.titleId        The id of the modal title
 *
 * @returns                    Component itself
 */
const ComposeModalHeader: FC<Props> = ({
  composeType,
  isDraftChanged,
  nodeType,
  onClose,
  title,
  titleId,
}) => {
  const isOpen = useIsComposeOpen();
  const location = useLocation();
  const navigate = useNavigate();

  const isPost = composeType === 'post';
  const isMessage = nodeType === 'MESSAGE';

  /**
   * Toggle the modal between minimized and maximized
   */
  const toggleModal = () => {
    navigate(getComposeMinimizeToggleUrl(location));
  };

  return (
    <Styled.ModalHeader data-is-post={isPost}>
      <Styled.ModalHeaderMainContent>
        {composeType !== null && <MuiIcon icon={composeType} />}
        <Styled.ModalTitle id={titleId} title={title}>
          {title}
        </Styled.ModalTitle>
      </Styled.ModalHeaderMainContent>

      <Styled.ModalHeaderActions>
        {isMessage && (
          <Tooltip
            title={translate(
              isOpen ? 'GENERAL__MINIMISE' : 'GENERAL__MAXIMISE',
            )}
          >
            <Styled.ActionButton
              disableFocusRipple={true}
              onClick={toggleModal}
            >
              <MuiIcon icon={isOpen ? 'minimize' : 'maximize'} />
            </Styled.ActionButton>
          </Tooltip>
        )}
        <ConfirmPopover
          onConfirm={onClose}
          shouldConfirm={isDraftChanged}
          trigger={
            <Tooltip title={translate('GENERAL__CLOSE')}>
              <Styled.ActionButton disableFocusRipple={true}>
                <MuiIcon icon="close" />
              </Styled.ActionButton>
            </Tooltip>
          }
          type={DialogId.ComposeClose}
        />
      </Styled.ModalHeaderActions>
    </Styled.ModalHeader>
  );
};

export default ComposeModalHeader;
